// Developer tools helpers: screen PPI, JWT expiration, API rate limits,
// cron scheduling, password entropy, page load time, uptime/SLA, unix
// timestamps, subnet/CIDR math, AI token cost, deployment risk, and WCAG
// color contrast.
import { t } from './i18n'

// "Screen PPI (Pixels Per Inch) Calculator"
export function ppiCalculator(params: { screen_width_px: unknown; screen_height_px: unknown; diagonal_inches: unknown }): number {
    const widthPx = Number(params.screen_width_px) || 0
    const heightPx = Number(params.screen_height_px) || 0
    const diagonalInches = Number(params.diagonal_inches) || 0
    if (diagonalInches <= 0) return 0
    const diagPx = Math.sqrt(widthPx ** 2 + heightPx ** 2)
    return diagPx / diagonalInches
}

// "JWT Expiration Calculator" - decodes a JWT's payload (base64url, no
// signature verification since this is a client-side informational tool,
// not an auth check) and reports its issued/expiry times.
const JWT_STATUS_VALID: Record<string, string> = {
    en: 'Valid (not expired)', ru: 'Действителен (не истёк)', de: 'Gültig (nicht abgelaufen)', es: 'Válido (no caducado)', fr: 'Valide (non expiré)', it: 'Valido (non scaduto)', pl: 'Ważny (nie wygasł)', lv: 'Derīgs (nav beidzies)',
}
const JWT_STATUS_EXPIRED: Record<string, string> = {
    en: 'Expired', ru: 'Истёк', de: 'Abgelaufen', es: 'Caducado', fr: 'Expiré', it: 'Scaduto', pl: 'Wygasł', lv: 'Beidzies',
}
const JWT_STATUS_NO_EXP: Record<string, string> = {
    en: 'No expiration claim found', ru: 'Claim истечения не найден', de: 'Kein Ablaufanspruch gefunden', es: 'No se encontró la reclamación de expiración', fr: 'Aucune revendication d\'expiration trouvée', it: 'Nessuna rivendicazione di scadenza trovata', pl: 'Nie znaleziono roszczenia o wygaśnięciu', lv: 'Derīguma termiņa apgalvojums nav atrasts',
}
const JWT_INVALID: Record<string, string> = {
    en: 'Invalid JWT format', ru: 'Неверный формат JWT', de: 'Ungültiges JWT-Format', es: 'Formato JWT no válido', fr: 'Format JWT invalide', it: 'Formato JWT non valido', pl: 'Nieprawidłowy format JWT', lv: 'Nederīgs JWT formāts',
}
function base64UrlDecode(str: string): string {
    let s = str.replace(/-/g, '+').replace(/_/g, '/')
    while (s.length % 4) s += '='
    return typeof atob === 'function' ? decodeURIComponent(atob(s).split('').map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')) : Buffer.from(s, 'base64').toString('utf8')
}
export function jwtExpirationCalculator(params: { jwt_token: unknown; language: unknown }): {
    issued_at: string; expires_at: string; time_remaining_seconds: number; status: string
} {
    const token = String(params.jwt_token || '').trim()
    const parts = token.split('.')
    if (parts.length < 2) {
        return { issued_at: '-', expires_at: '-', time_remaining_seconds: 0, status: t(JWT_INVALID, params.language) }
    }
    try {
        const payload = JSON.parse(base64UrlDecode(parts[1]))
        const nowSec = Math.floor(Date.now() / 1000)
        const issuedAt = typeof payload.iat === 'number' ? new Date(payload.iat * 1000).toISOString() : '-'
        if (typeof payload.exp !== 'number') {
            return { issued_at: issuedAt, expires_at: '-', time_remaining_seconds: 0, status: t(JWT_STATUS_NO_EXP, params.language) }
        }
        const expiresAt = new Date(payload.exp * 1000).toISOString()
        const remaining = payload.exp - nowSec
        const status = remaining > 0 ? t(JWT_STATUS_VALID, params.language) : t(JWT_STATUS_EXPIRED, params.language)
        return { issued_at: issuedAt, expires_at: expiresAt, time_remaining_seconds: remaining, status }
    } catch {
        return { issued_at: '-', expires_at: '-', time_remaining_seconds: 0, status: t(JWT_INVALID, params.language) }
    }
}

// "API Rate Limit Calculator"
export function apiRateLimitCalculator(params: { limit: unknown; window_minutes: unknown; current_usage: unknown }): {
    remaining_requests: number; usage_percent: number; safe_requests_per_second: number
} {
    const limit = Number(params.limit) || 0
    const windowMinutes = Number(params.window_minutes) || 1
    const currentUsage = Number(params.current_usage) || 0
    return {
        remaining_requests: Math.max(limit - currentUsage, 0),
        usage_percent: limit > 0 ? (currentUsage / limit) * 100 : 0,
        safe_requests_per_second: limit / (windowMinutes * 60),
    }
}

// "Cron Next Run Time Calculator" - supports standard 5-field cron syntax
// (*, lists, ranges, steps). Operates in UTC, since server crontabs are
// conventionally scheduled in UTC and this avoids browser-timezone ambiguity.
const CRON_INVALID: Record<string, string> = {
    en: 'Invalid cron expression', ru: 'Неверное cron-выражение', de: 'Ungültiger Cron-Ausdruck', es: 'Expresión cron no válida', fr: 'Expression cron invalide', it: 'Espressione cron non valida', pl: 'Nieprawidłowe wyrażenie cron', lv: 'Nederīga cron izteiksme',
}
function parseCronField(field: string, min: number, max: number): number[] {
    if (field === '*') {
        const arr: number[] = []
        for (let i = min; i <= max; i++) arr.push(i)
        return arr
    }
    const values = new Set<number>()
    for (const part of field.split(',')) {
        if (part.includes('/')) {
            const [range, stepStr] = part.split('/')
            const step = parseInt(stepStr, 10)
            let start = min
            let end = max
            if (range !== '*') {
                if (range.includes('-')) {
                    const [s, e] = range.split('-').map(Number)
                    start = s
                    end = e
                } else {
                    start = Number(range)
                }
            }
            for (let i = start; i <= end; i += step) values.add(i)
        } else if (part.includes('-')) {
            const [start, end] = part.split('-').map(Number)
            for (let i = start; i <= end; i++) values.add(i)
        } else {
            values.add(Number(part))
        }
    }
    return Array.from(values).sort((a, b) => a - b)
}
function cronMatchesUtc(date: Date, minutes: number[], hours: number[], days: number[], months: number[], weekdays: number[]): boolean {
    return minutes.includes(date.getUTCMinutes()) &&
        hours.includes(date.getUTCHours()) &&
        days.includes(date.getUTCDate()) &&
        months.includes(date.getUTCMonth() + 1) &&
        weekdays.includes(date.getUTCDay())
}
export function cronNextRunCalculator(params: { cron_expression: unknown; language: unknown }): {
    next_run_1: string; next_run_2: string; next_run_3: string
} {
    const expr = String(params.cron_expression || '').trim()
    const fields = expr.split(/\s+/)
    if (fields.length !== 5) {
        const err = t(CRON_INVALID, params.language)
        return { next_run_1: err, next_run_2: err, next_run_3: err }
    }
    try {
        const [min, hr, dom, mon, dow] = fields
        const minutes = parseCronField(min, 0, 59)
        const hours = parseCronField(hr, 0, 23)
        const days = parseCronField(dom, 1, 31)
        const months = parseCronField(mon, 1, 12)
        const weekdays = parseCronField(dow, 0, 6)
        const results: Date[] = []
        const cur = new Date()
        cur.setUTCSeconds(0, 0)
        cur.setUTCMinutes(cur.getUTCMinutes() + 1)
        let iterations = 0
        const maxIterations = 60 * 24 * 366 * 2
        while (results.length < 3 && iterations < maxIterations) {
            if (cronMatchesUtc(cur, minutes, hours, days, months, weekdays)) results.push(new Date(cur))
            cur.setUTCMinutes(cur.getUTCMinutes() + 1)
            iterations++
        }
        const err = t(CRON_INVALID, params.language)
        return {
            next_run_1: results[0] ? results[0].toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : err,
            next_run_2: results[1] ? results[1].toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : err,
            next_run_3: results[2] ? results[2].toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : err,
        }
    } catch {
        const err = t(CRON_INVALID, params.language)
        return { next_run_1: err, next_run_2: err, next_run_3: err }
    }
}

// "Password Entropy & Crack Time Calculator" - detects character classes
// actually present in the entered password (rather than asking the user to
// declare them via toggles), then estimates entropy and average-case
// offline crack time at a given guess rate.
const STRENGTH_VERY_WEAK: Record<string, string> = {
    en: 'Very Weak', ru: 'Очень слабый', de: 'Sehr schwach', es: 'Muy débil', fr: 'Très faible', it: 'Molto debole', pl: 'Bardzo słabe', lv: 'Ļoti vājš',
}
const STRENGTH_WEAK: Record<string, string> = {
    en: 'Weak', ru: 'Слабый', de: 'Schwach', es: 'Débil', fr: 'Faible', it: 'Debole', pl: 'Słabe', lv: 'Vājš',
}
const STRENGTH_MODERATE: Record<string, string> = {
    en: 'Moderate', ru: 'Средний', de: 'Mittel', es: 'Moderado', fr: 'Modéré', it: 'Moderato', pl: 'Umiarkowane', lv: 'Vidējs',
}
const STRENGTH_STRONG: Record<string, string> = {
    en: 'Strong', ru: 'Сильный', de: 'Stark', es: 'Fuerte', fr: 'Fort', it: 'Forte', pl: 'Silne', lv: 'Spēcīgs',
}
const STRENGTH_VERY_STRONG: Record<string, string> = {
    en: 'Very Strong', ru: 'Очень сильный', de: 'Sehr stark', es: 'Muy fuerte', fr: 'Très fort', it: 'Molto forte', pl: 'Bardzo silne', lv: 'Ļoti spēcīgs',
}
export function passwordEntropyCalculator(params: { password: unknown; guesses_per_second: unknown; language: unknown }): {
    entropy_bits: number; crack_time_years: number; strength: string
} {
    const password = String(params.password || '')
    const guessesPerSecond = Number(params.guesses_per_second) || 1e10
    let poolSize = 0
    if (/[a-z]/.test(password)) poolSize += 26
    if (/[A-Z]/.test(password)) poolSize += 26
    if (/[0-9]/.test(password)) poolSize += 10
    if (/[^a-zA-Z0-9]/.test(password)) poolSize += 32
    const length = password.length
    const entropyBits = length > 0 ? length * Math.log2(poolSize || 1) : 0
    const combinations = Math.pow(poolSize || 1, length)
    const crackTimeSeconds = length > 0 ? combinations / (2 * guessesPerSecond) : 0
    const crackTimeYears = crackTimeSeconds / (365.25 * 24 * 3600)
    let strength: Record<string, string>
    if (entropyBits < 28) strength = STRENGTH_VERY_WEAK
    else if (entropyBits < 36) strength = STRENGTH_WEAK
    else if (entropyBits < 60) strength = STRENGTH_MODERATE
    else if (entropyBits < 90) strength = STRENGTH_STRONG
    else strength = STRENGTH_VERY_STRONG
    return { entropy_bits: entropyBits, crack_time_years: crackTimeYears, strength: t(strength, params.language) }
}

// "Website Page Load Time Calculator" - simple bandwidth-bound estimate,
// not accounting for latency/TLS handshake/render time
const LOAD_EXCELLENT: Record<string, string> = {
    en: 'Excellent', ru: 'Отлично', de: 'Ausgezeichnet', es: 'Excelente', fr: 'Excellent', it: 'Eccellente', pl: 'Doskonała', lv: 'Izcila',
}
const LOAD_GOOD: Record<string, string> = {
    en: 'Good', ru: 'Хорошо', de: 'Gut', es: 'Bueno', fr: 'Bon', it: 'Buono', pl: 'Dobra', lv: 'Laba',
}
const LOAD_FAIR: Record<string, string> = {
    en: 'Fair', ru: 'Приемлемо', de: 'Ausreichend', es: 'Aceptable', fr: 'Acceptable', it: 'Accettabile', pl: 'Zadowalająca', lv: 'Pieņemama',
}
const LOAD_POOR: Record<string, string> = {
    en: 'Poor (users may leave before it loads)', ru: 'Плохо (пользователи могут уйти до загрузки)', de: 'Schlecht (Nutzer könnten vorher abspringen)', es: 'Deficiente (los usuarios pueden irse antes de que cargue)', fr: 'Mauvais (les utilisateurs peuvent partir avant le chargement)', it: 'Scarso (gli utenti potrebbero andarsene prima del caricamento)', pl: 'Słaba (użytkownicy mogą odejść przed załadowaniem)', lv: 'Slikta (lietotāji var pamest pirms ielādes)',
}
export function pageLoadTimeCalculator(params: { page_size_mb: unknown; connection_speed_mbps: unknown; language: unknown }): {
    load_time_seconds: number; rating: string
} {
    const pageSizeMb = Number(params.page_size_mb) || 0
    const connectionMbps = Number(params.connection_speed_mbps) || 1
    const loadTimeSeconds = (pageSizeMb * 8) / connectionMbps
    let rating: Record<string, string>
    if (loadTimeSeconds < 1) rating = LOAD_EXCELLENT
    else if (loadTimeSeconds < 2.5) rating = LOAD_GOOD
    else if (loadTimeSeconds < 4) rating = LOAD_FAIR
    else rating = LOAD_POOR
    return { load_time_seconds: loadTimeSeconds, rating: t(rating, params.language) }
}

// "Uptime / SLA Downtime Calculator"
export function slaDowntimeCalculator(params: { uptime_percent: unknown }): {
    downtime_per_day_seconds: number; downtime_per_month_minutes: number; downtime_per_year_hours: number
} {
    const uptimePercent = Number(params.uptime_percent) || 0
    const downFraction = Math.max(1 - uptimePercent / 100, 0)
    return {
        downtime_per_day_seconds: downFraction * 24 * 3600,
        downtime_per_month_minutes: downFraction * 30.44 * 24 * 60,
        downtime_per_year_hours: downFraction * 365.25 * 24,
    }
}

// "Unix Timestamp Converter" - two-way; `mode` selects which direction, and
// only the relevant output is populated (the other is left undefined, which
// CalculatorWidget already skips rendering, same pattern used elsewhere)
export function unixTimestampConverter(params: { mode: unknown; unix_timestamp: unknown; date_string: unknown }): {
    formatted_date?: string; unix_timestamp_result?: number
} {
    const mode = String(params.mode)
    if (mode === 'to_date') {
        const unixSeconds = Number(params.unix_timestamp) || 0
        const date = new Date(unixSeconds * 1000)
        return { formatted_date: isNaN(date.getTime()) ? '-' : date.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' }
    }
    const parsed = new Date(String(params.date_string))
    return { unix_timestamp_result: isNaN(parsed.getTime()) ? 0 : Math.floor(parsed.getTime() / 1000) }
}

// "Subnet / CIDR Calculator" - IPv4 only
function ipToInt(ip: string): number | null {
    const parts = ip.split('.').map(Number)
    if (parts.length !== 4 || parts.some((p) => isNaN(p) || p < 0 || p > 255)) return null
    return parts.reduce((acc, octet) => (acc << 8) + octet, 0) >>> 0
}
function intToIp(int: number): string {
    return [(int >>> 24) & 255, (int >>> 16) & 255, (int >>> 8) & 255, int & 255].join('.')
}
export function subnetCalculator(params: { ip_address: unknown; cidr_prefix: unknown }): {
    subnet_mask: string; network_address: string; broadcast_address: string; total_hosts: number; usable_hosts: number; first_usable: string; last_usable: string
} {
    const ip = String(params.ip_address || '')
    const prefix = Math.min(Math.max(Number(params.cidr_prefix) || 0, 0), 32)
    const ipInt = ipToInt(ip)
    if (ipInt === null) {
        return { subnet_mask: '-', network_address: '-', broadcast_address: '-', total_hosts: 0, usable_hosts: 0, first_usable: '-', last_usable: '-' }
    }
    const maskInt = prefix === 0 ? 0 : (0xFFFFFFFF << (32 - prefix)) >>> 0
    const networkInt = (ipInt & maskInt) >>> 0
    const broadcastInt = (networkInt | (~maskInt >>> 0)) >>> 0
    const totalHosts = Math.pow(2, 32 - prefix)
    const usableHosts = Math.max(totalHosts - 2, 0)
    return {
        subnet_mask: intToIp(maskInt),
        network_address: intToIp(networkInt),
        broadcast_address: intToIp(broadcastInt),
        total_hosts: totalHosts,
        usable_hosts: usableHosts,
        first_usable: usableHosts > 0 ? intToIp(networkInt + 1) : intToIp(networkInt),
        last_usable: usableHosts > 0 ? intToIp(broadcastInt - 1) : intToIp(broadcastInt),
    }
}

// "My IP Range Calculator" - auto-detects the visitor's own public IP
// (client_ip, injected the same way as ipAddressDetector in
// gamesRandomizersCalculators.ts) and applies the same CIDR math as
// subnetCalculator above, framed for a distinct use case: pasting a stable
// CIDR range into a GA4/analytics "internal traffic" IP filter, since a
// single exact-IP filter breaks the moment a dynamic ISP IP changes.
export function myIpRangeCalculator(params: { client_ip: unknown; cidr_prefix: unknown; language: unknown }): {
    your_ip: string; cidr_range: string; subnet_mask: string; broadcast_address: string; total_addresses: number; steps: string
} {
    const ip = String(params.client_ip || '')
    const prefix = Math.min(Math.max(Number(params.cidr_prefix) || 0, 0), 32)
    const ipInt = ipToInt(ip)

    if (ipInt === null) {
        const errText = t(IP_DETECTION_FAILED_LABEL, params.language)
        return { your_ip: ip || '-', cidr_range: '-', subnet_mask: '-', broadcast_address: '-', total_addresses: 0, steps: errText }
    }

    const maskInt = prefix === 0 ? 0 : (0xFFFFFFFF << (32 - prefix)) >>> 0
    const networkInt = (ipInt & maskInt) >>> 0
    const broadcastInt = (networkInt | (~maskInt >>> 0)) >>> 0
    const totalAddresses = Math.pow(2, 32 - prefix)
    const networkIp = intToIp(networkInt)
    const cidrRange = `${networkIp}/${prefix}`

    const steps = [
        `${t(STEP_DETECTED_IP_LABEL, params.language)} = ${ip}`,
        `${t(STEP_SUBNET_MASK_LABEL, params.language)} /${prefix} = ${intToIp(maskInt)}`,
        `${t(STEP_NETWORK_ADDRESS_LABEL, params.language)} = ${networkIp}`,
        `${t(STEP_CIDR_RANGE_LABEL, params.language)} = ${cidrRange} (${totalAddresses.toLocaleString('en-US')} ${t(STEP_ADDRESSES_LABEL, params.language)}, ${networkIp} - ${intToIp(broadcastInt)})`,
    ].join('\n')

    return {
        your_ip: ip,
        cidr_range: cidrRange,
        subnet_mask: intToIp(maskInt),
        broadcast_address: intToIp(broadcastInt),
        total_addresses: totalAddresses,
        steps,
    }
}
const IP_DETECTION_FAILED_LABEL: Record<string, string> = {
    en: 'Could not detect a valid public IPv4 address for this connection.',
    ru: 'Не удалось определить корректный публичный IPv4-адрес для этого соединения.',
    lv: 'Neizdevās noteikt derīgu publisko IPv4 adresi šim savienojumam.',
    pl: 'Nie udało się wykryć prawidłowego publicznego adresu IPv4 dla tego połączenia.',
    es: 'No se pudo detectar una dirección IPv4 pública válida para esta conexión.',
    fr: 'Impossible de détecter une adresse IPv4 publique valide pour cette connexion.',
    it: 'Impossibile rilevare un indirizzo IPv4 pubblico valido per questa connessione.',
    de: 'Für diese Verbindung konnte keine gültige öffentliche IPv4-Adresse ermittelt werden.',
}
const STEP_DETECTED_IP_LABEL: Record<string, string> = {
    en: 'Detected public IP', ru: 'Обнаруженный публичный IP', lv: 'Noteiktais publiskais IP', pl: 'Wykryty publiczny IP',
    es: 'IP pública detectada', fr: 'IP publique détectée', it: 'IP pubblico rilevato', de: 'Erkannte öffentliche IP',
}
const STEP_SUBNET_MASK_LABEL: Record<string, string> = {
    en: 'Subnet mask for', ru: 'Маска подсети для', lv: 'Apakštīkla maska priekš', pl: 'Maska podsieci dla',
    es: 'Máscara de subred para', fr: 'Masque de sous-réseau pour', it: 'Maschera di sottorete per', de: 'Subnetzmaske für',
}
const STEP_NETWORK_ADDRESS_LABEL: Record<string, string> = {
    en: 'Network address', ru: 'Адрес сети', lv: 'Tīkla adrese', pl: 'Adres sieci',
    es: 'Dirección de red', fr: 'Adresse réseau', it: 'Indirizzo di rete', de: 'Netzwerkadresse',
}
const STEP_CIDR_RANGE_LABEL: Record<string, string> = {
    en: 'CIDR range', ru: 'Диапазон CIDR', lv: 'CIDR diapazons', pl: 'Zakres CIDR',
    es: 'Rango CIDR', fr: 'Plage CIDR', it: 'Intervallo CIDR', de: 'CIDR-Bereich',
}
const STEP_ADDRESSES_LABEL: Record<string, string> = {
    en: 'addresses', ru: 'адресов', lv: 'adreses', pl: 'adresów',
    es: 'direcciones', fr: 'adresses', it: 'indirizzi', de: 'Adressen',
}

// "AI/LLM Token Cost Calculator" - token count is a rough heuristic
// (~4 characters per token, a commonly cited approximation for English text
// with GPT-style tokenizers), not an exact tokenizer run
export function aiTokenCostCalculator(params: { input_text: unknown; price_per_million_tokens: unknown; num_requests: unknown }): {
    estimated_tokens: number; total_tokens: number; estimated_cost: number
} {
    const text = String(params.input_text || '')
    const pricePerMillion = Number(params.price_per_million_tokens) || 0
    const numRequests = Math.max(Number(params.num_requests) || 1, 1)
    const estimatedTokens = Math.ceil(text.length / 4)
    const totalTokens = estimatedTokens * numRequests
    const estimatedCost = (totalTokens / 1e6) * pricePerMillion
    return { estimated_tokens: estimatedTokens, total_tokens: totalTokens, estimated_cost: estimatedCost }
}

// "Deployment Risk (Change Failure Rate) Calculator" - risk bands loosely
// inspired by DORA-style change-failure-rate benchmarking, not an exact
// reproduction of any single published report's thresholds
const RISK_LOW: Record<string, string> = {
    en: 'Low Risk', ru: 'Низкий риск', de: 'Geringes Risiko', es: 'Riesgo Bajo', fr: 'Risque Faible', it: 'Rischio Basso', pl: 'Niskie Ryzyko', lv: 'Zems Risks',
}
const RISK_MODERATE: Record<string, string> = {
    en: 'Moderate Risk', ru: 'Умеренный риск', de: 'Mittleres Risiko', es: 'Riesgo Moderado', fr: 'Risque Modéré', it: 'Rischio Moderato', pl: 'Umiarkowane Ryzyko', lv: 'Vidējs Risks',
}
const RISK_HIGH: Record<string, string> = {
    en: 'High Risk', ru: 'Высокий риск', de: 'Hohes Risiko', es: 'Riesgo Alto', fr: 'Risque Élevé', it: 'Rischio Alto', pl: 'Wysokie Ryzyko', lv: 'Augsts Risks',
}
const RISK_CRITICAL: Record<string, string> = {
    en: 'Critical Risk', ru: 'Критический риск', de: 'Kritisches Risiko', es: 'Riesgo Crítico', fr: 'Risque Critique', it: 'Rischio Critico', pl: 'Krytyczne Ryzyko', lv: 'Kritisks Risks',
}
export function deploymentRiskCalculator(params: { deployments_per_month: unknown; failed_deployments: unknown; language: unknown }): {
    change_failure_rate: number; deployments_per_day: number; risk_level: string
} {
    const deploymentsPerMonth = Number(params.deployments_per_month) || 0
    const failedDeployments = Number(params.failed_deployments) || 0
    const changeFailureRate = deploymentsPerMonth > 0 ? (failedDeployments / deploymentsPerMonth) * 100 : 0
    const deploymentsPerDay = deploymentsPerMonth / 30
    let riskLevel: Record<string, string>
    if (changeFailureRate < 10) riskLevel = RISK_LOW
    else if (changeFailureRate < 25) riskLevel = RISK_MODERATE
    else if (changeFailureRate < 50) riskLevel = RISK_HIGH
    else riskLevel = RISK_CRITICAL
    return { change_failure_rate: changeFailureRate, deployments_per_day: deploymentsPerDay, risk_level: t(riskLevel, params.language) }
}

// "Color Contrast Ratio (WCAG) Calculator"
const CONTRAST_AAA: Record<string, string> = {
    en: 'Passes AAA (all text sizes)', ru: 'Соответствует AAA (весь текст)', de: 'Erfüllt AAA (alle Textgrößen)', es: 'Cumple AAA (todos los tamaños de texto)', fr: 'Conforme AAA (toutes tailles de texte)', it: 'Conforme AAA (tutte le dimensioni di testo)', pl: 'Spełnia AAA (wszystkie rozmiary tekstu)', lv: 'Atbilst AAA (visi teksta izmēri)',
}
const CONTRAST_AA: Record<string, string> = {
    en: 'Passes AA (all text), AAA (large text only)', ru: 'Соответствует AA (весь текст), AAA (крупный текст)', de: 'Erfüllt AA (aller Text), AAA (nur großer Text)', es: 'Cumple AA (todo el texto), AAA (solo texto grande)', fr: 'Conforme AA (tout texte), AAA (texte large uniquement)', it: 'Conforme AA (tutto il testo), AAA (solo testo grande)', pl: 'Spełnia AA (cały tekst), AAA (tylko duży tekst)', lv: 'Atbilst AA (viss teksts), AAA (tikai liels teksts)',
}
const CONTRAST_AA_LARGE: Record<string, string> = {
    en: 'Passes AA (large text only)', ru: 'Соответствует AA (только крупный текст)', de: 'Erfüllt AA (nur großer Text)', es: 'Cumple AA (solo texto grande)', fr: 'Conforme AA (texte large uniquement)', it: 'Conforme AA (solo testo grande)', pl: 'Spełnia AA (tylko duży tekst)', lv: 'Atbilst AA (tikai liels teksts)',
}
const CONTRAST_FAIL: Record<string, string> = {
    en: 'Fails WCAG AA', ru: 'Не соответствует WCAG AA', de: 'Erfüllt WCAG AA nicht', es: 'No cumple WCAG AA', fr: 'Ne respecte pas WCAG AA', it: 'Non conforme a WCAG AA', pl: 'Nie spełnia WCAG AA', lv: 'Neatbilst WCAG AA',
}
function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const h = hex.replace('#', '').trim()
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
    const num = parseInt(full, 16) || 0
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}
function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
    const channels = [r, g, b].map((c) => {
        const s = c / 255
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}
export function colorContrastCalculator(params: { foreground_hex: unknown; background_hex: unknown; language: unknown }): {
    contrast_ratio: number; wcag_rating: string
} {
    const l1 = relativeLuminance(hexToRgb(String(params.foreground_hex || '#000000')))
    const l2 = relativeLuminance(hexToRgb(String(params.background_hex || '#FFFFFF')))
    const lighter = Math.max(l1, l2)
    const darker = Math.min(l1, l2)
    const ratio = (lighter + 0.05) / (darker + 0.05)
    let rating: Record<string, string>
    if (ratio >= 7) rating = CONTRAST_AAA
    else if (ratio >= 4.5) rating = CONTRAST_AA
    else if (ratio >= 3) rating = CONTRAST_AA_LARGE
    else rating = CONTRAST_FAIL
    return { contrast_ratio: ratio, wcag_rating: t(rating, params.language) }
}
